import { keyframes, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent } from 'react';

import { Card } from '~/ui/core/card/Card';

const shimmer = keyframes`
    0% {
        background-position: -1000px 0;
    }
    100% {
        background-position: 1000px 0;
    }
`;

const SkeletonBase = styled.div(({ theme }) => ({
    animation: `${shimmer} 2s ease-in-out infinite`,
    background: `linear-gradient(90deg, ${theme.colors.gray[200]} 0%, ${theme.colors.gray[300]} 50%, ${theme.colors.gray[200]} 100%)`,
    backgroundSize: '1000px 100%',
    borderRadius: theme.borderRadius.m,
}));

const SkeletonTitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.xl,
    width: '60%',
}));

const SkeletonContent = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.xl,
    width: '20%',
}));

const StyledCard = styled(Card)(({ theme }) => ({
    backgroundColor: theme.colors.white[200],
    border: '1px solid transparent',
    borderRadius: theme.borderRadius.xl,
    boxShadow: theme.shadows.small,
    gap: theme.spacing.xl,
    minHeight: '60px',
    padding: theme.spacing.m,
    width: '100%',
}));

type SkeletonCardsProps = {
    readonly count?: number;
};

export const SkeletonCard: FunctionComponent = () => {
    return (
        <StyledCard data-testid="loading-skeleton">
            <SkeletonTitle />
            <SkeletonContent />
        </StyledCard>
    );
};

export const SkeletonCards: FunctionComponent<SkeletonCardsProps> = ({ count = 1 }) => {
    const theme = useTheme();

    return (
        <AnimatePresence mode="popLayout">
            <motion.div
                key="skeleton-cards-container"
                animate="visible"
                exit="hidden"
                initial="hidden"
                variants={{
                    exit: {
                        transition: {
                            staggerChildren: 0.05,
                            staggerDirection: -1,
                        },
                    },
                    hidden: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing.m,
                    },
                    visible: {
                        transition: {
                            delayChildren: 0,
                            staggerChildren: 0.1,
                        },
                    },
                }}
            >
                {Array.from({ length: count }, (_, index) => (
                    <motion.div
                        // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                        key={index}
                        variants={{
                            hidden: { opacity: 0, scale: 0.95, x: -50 },
                            visible: { opacity: 1, scale: 1, x: 0 },
                        }}
                    >
                        <SkeletonCard />
                    </motion.div>
                ))}
            </motion.div>
        </AnimatePresence>
    );
};
